import { createSelector } from 'reselect';
import { getFormValues } from 'redux-form';
import groupBy from 'lodash/groupBy';
import toArray from 'lodash/toArray';

import { RootState } from '../../reducers';
import { InventoryReportData, StockTransactionItem } from './inventory.type';
import { Report, ReportViewSettings } from '../reports/reports.types';
import { Forms } from '../../form/form.selector';
import { ReportsFilterForm } from '../../../routes/Reports/ReportsPage/ReportsSpecificPage/ReportsSpecificPageToolbar/ReportsSpecificPageToolbar';
import { getCodeList } from '../code/code.selector';
import { CodeTypeNames } from '../code/code.types';

export const getInventoryProjectedRequirementsReport = (state: RootState): Report<InventoryReportData> | null =>
  state.entities.reports.inventoryrequirements;

export const getInventoryTransactionsReport = (state: RootState): Report<StockTransactionItem> | null =>
  state.entities.reports.stocktransaction;

export const getInventoryProjectedRequirementsReportFilteredBySearch = createSelector(
  [getInventoryProjectedRequirementsReport, getFormValues(Forms.projectedRequirementsForm)],
  (data: any, filterForm: ReportsFilterForm) => {
    if (!data) {
      return { items: [] };
    }

    if (!filterForm || !filterForm.search) {
      return data.reportData.gridData;
    }

    return {
      ...data.reportData!.gridData,
      items: data.reportData!.gridData?.items.filter((elem) =>
        elem.ingredientName.toLowerCase().includes(filterForm.search.toLowerCase()),
      ),
    };
  },
);

export const getInventoryTransactionsReportGroupSummaryValues = createSelector(
  [getInventoryTransactionsReport],
  (report: Report<StockTransactionItem> | null) => {
    if (!report || !report.reportData || !report.reportData.gridData || !report.reportData.gridData.groupSummaries) {
      return null;
    }
    return report.reportData.gridData.groupSummaries;
  },
);

export const getInventoryTransactionsReportSummaryItems = createSelector(
  [getInventoryTransactionsReport],
  (report: Report<StockTransactionItem> | null) => {
    if (!report || !report.reportData || !report.reportData.gridData || !report.reportData.gridData.summaryItems) {
      return null;
    }
    return report.reportData.gridData.summaryItems;
  },
);

export const getInventoryTransactionsReportSummaryItemWeight = createSelector(
  [getInventoryTransactionsReportSummaryItems, getCodeList],
  (summaryItems, codeList) => {
    if (!summaryItems) {
      return null;
    }
    const priceUnit = summaryItems[0].priceUnit;
    const priceUnitCodes = codeList[CodeTypeNames.priceUnitCodes] || [];
    return priceUnitCodes.find((code) => code.abbr === priceUnit);
  },
);

export const getInventoryTransactionsReportGroupedData = createSelector(
  [getInventoryTransactionsReport],
  (report: Report<StockTransactionItem> | null) => {
    if (!report || !report.reportData || !report.reportData.gridData || !report.reportData.gridData.items) {
      return null;
    }
    const groupedArray = toArray(
      groupBy(
        [...report.reportData.gridData.items]
          .sort((item1, item2) => item1.ingredientDescription.localeCompare(item2.ingredientDescription))
          .sort((item1, item2) => {
            if (item1.ingredientDescription === item2.ingredientDescription) {
              return new Date(item1.actualDate) > new Date(item2.actualDate) ? 1 : -1;
            }
            return 0;
          }),
        'ingredientId',
      ),
    );
    return groupedArray;
  },
);

export const getInventoryTransactionsReportFilteredBySearch = createSelector(
  [getInventoryTransactionsReportGroupedData, getFormValues(Forms.projectedRequirementsForm)],
  (data: StockTransactionItem[][] | null, filterForm: ReportsFilterForm) => {
    if (!data) {
      return [];
    }

    if (!filterForm || !filterForm.search) {
      return data;
    }

    return data
      .map((array) =>
        array.filter(
          (item) =>
            item.referenceNo.includes(filterForm.search) ||
            item.invoiceNo.includes(filterForm.search) ||
            item.requestNo.toString().includes(filterForm.search),
        ),
      )
      .filter((array) => array.length !== 0);
  },
);

export const getInventoryTransactionsViewSettings = createSelector(
  [getInventoryTransactionsReport],
  (reportData: Report<StockTransactionItem>): ReportViewSettings | null => {
    if (!reportData) {
      return null;
    }
    return reportData.viewSettings;
  },
);

export const getInventorySelectedTimeRange = createSelector(
  [getFormValues(Forms.reportsPageForm) as any],
  (reportsFormValues: any): any | null => {
    const selectedTimeRange = { startDate: reportsFormValues?.startDate, endDate: reportsFormValues?.endDate };
    return reportsFormValues ? selectedTimeRange : null;
  },
);

export const getTransactionsSelectedTimeRange = createSelector(
  [getFormValues(Forms.reportsPageForm) as any],
  (reportsFormValues: any): any | null => {
    const selectedTimeRange = {
      labelKey: reportsFormValues?.selectedTimeRange?.labelKey,
      startDate: reportsFormValues?.selectedTimeRange?.startDate,
      endDate: reportsFormValues?.selectedTimeRange?.endDate,
    };
    return reportsFormValues ? selectedTimeRange : null;
  },
);
